import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const sectionNav = [
  { label: "头条解读", id: "headline", key: "headline" },
  { label: "重点瓜分析", id: "analysis", key: "analysis" },
  { label: "深度报道", id: "deep", key: "deep" },
  { label: "封面故事", id: "cover", key: "cover" },
  { label: "编辑推荐", id: "pick", key: "pick" },
  { label: "关于瓜报", id: "about", key: "about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.hash]);

  const goTo = (id: string) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 80);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const submitSearch = () => {
    if (!keyword.trim()) return;
    setSearchOpen(false);
    setKeyword("");
    // 站内检索入口：跳转首页并高亮相关区块
    navigate("/");
    setTimeout(() => {
      const target = document.getElementById("feed");
      if (target) {
        target.scrollIntoView({ behavior: "smooth" });
        window.dispatchEvent(new CustomEvent("guabao:search", { detail: keyword }));
      }
    }, 80);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || open ? "bg-background-50/95 backdrop-blur-md border-b border-background-200" : "bg-background-50"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        {/* 报头 */}
        <div className="flex items-center justify-between py-3">
          <Link to="/" className="flex items-center gap-2.5" aria-label="今日瓜报首页">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-goblet-fill text-lg" aria-hidden="true"></i>
            </span>
            <span className="leading-none">
              <span className="block font-serif text-xl md:text-2xl font-black tracking-tight text-foreground-950">
                今日瓜报
              </span>
              <span className="block mt-0.5 text-[10px] tracking-[0.3em] text-secondary-500 uppercase">deep scoop daily</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-1 rounded-full bg-background-100 px-1.5 py-1.5">
            {sectionNav.map((item) => (
              <button
                key={item.id}
                onClick={() => goTo(item.id)}
                className="px-3.5 py-1.5 text-sm text-foreground-700 hover:text-primary-700 whitespace-nowrap rounded-full hover:bg-background-50 transition-colors"
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1.5">
            <div className={`items-center ${searchOpen ? "flex" : "hidden"} md:flex relative`}>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitSearch()}
                placeholder="搜索想看的瓜…"
                aria-label="站内搜索"
                className="w-36 lg:w-48 h-9 rounded-full border border-background-200 bg-background-50 px-4 pr-9 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
              <button
                onClick={submitSearch}
                aria-label="搜索"
                className="absolute right-2 flex h-7 w-7 items-center justify-center rounded-full text-foreground-600 hover:text-primary-700"
              >
                <i className="ri-search-line text-sm" aria-hidden="true"></i>
              </button>
            </div>
            <button
              onClick={() => setSearchOpen((v) => !v)}
              className="md:hidden flex h-9 w-9 items-center justify-center rounded-full text-foreground-700 hover:bg-background-100"
              aria-label="展开搜索"
            >
              <i className={searchOpen ? "ri-close-line text-lg" : "ri-search-line text-lg"} aria-hidden="true"></i>
            </button>
            <button
              onClick={submitSearch}
              className="hidden md:flex items-center gap-1.5 whitespace-nowrap h-9 px-4 rounded-full bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors"
            >
              <i className="ri-download-cloud-2-line text-base" aria-hidden="true"></i>
              订阅瓜报
            </button>
            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden flex h-9 w-9 items-center justify-center rounded-full text-foreground-800 hover:bg-background-100"
              aria-label="打开菜单"
            >
              <i className={open ? "ri-close-line text-xl" : "ri-menu-line text-xl"} aria-hidden="true"></i>
            </button>
          </div>
        </div>

        {/* 副导航（桌面下方栏） */}
        <div className="hidden lg:flex items-center justify-between border-t border-background-200 py-2 text-xs text-foreground-600">
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1.5 text-primary-700">
              <i className="ri-flashlight-fill" aria-hidden="true"></i>
              <strong>热门栏目</strong>
            </span>
            <span className="mx-1 text-background-300">|</span>
            {["品牌舆情", "演出市场", "综艺产业", "媒体生态", "剧集市场", "公关行业", "人物专题"].map((t) => (
              <button key={t} onClick={() => goTo("feed")} className="hover:text-primary-700 whitespace-nowrap transition-colors">
                {t}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 whitespace-nowrap">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-2-line" aria-hidden="true"></i> 2026年9月6日 · 星期日
            </span>
            <button onClick={() => goTo("about")} className="hover:text-primary-700 transition-colors">
              关于我们
            </button>
            <button onClick={() => goTo("faq")} className="hover:text-primary-700 transition-colors">
              常见问题
            </button>
          </div>
        </div>
      </div>

      {/* 移动端菜单 */}
      {open && (
        <nav className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-4">
          <div className="grid grid-cols-2 gap-2">
            {sectionNav.map((item) => (
              <button
                key={item.id}
                onClick={() => goTo(item.id)}
                className="flex items-center gap-2 rounded-lg bg-background-100 px-4 py-3 text-left text-sm text-foreground-800 hover:bg-background-200"
              >
                <i className="ri-arrow-right-up-line text-primary-600" aria-hidden="true"></i>
                {item.label}
              </button>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                document.getElementById("subscribe")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="flex items-center justify-center gap-2 rounded-lg bg-primary-500 px-4 py-3 text-sm text-background-50 col-span-2"
            >
              <i className="ri-mail-send-line" aria-hidden="true"></i> 订阅每日推送
            </button>
          </div>
        </nav>
      )}
    </header>
  );
}